import {
  type FillLayerSpecification,
  type FilterSpecification,
  type LineLayerSpecification,
  type StyleSpecification,
} from '@aus-platform/cesium';
import { type ImageryLayer } from 'cesium';
import { cloneDeep, isEmpty } from 'lodash';
import { type CesiumProxy } from 'src/shared/cesium';
import { type WorkspaceLayer } from 'src/map-3d/components/map-3d-sidebar/shared';

const TEXTBOX_TYPE_VALUE = 'TextBox';
const LABEL_LAYER_ID = 'label';

/**
 * Folds hidden-feature conditions into the style so the first tile the MVT
 * provider paints already excludes them. Applying the same filters after the
 * provider exists is too late: Cesium has already requested unfiltered tiles.
 */
export const applyHiddenFeatureFiltersToStyle = (
  style: StyleSpecification,
  hiddenFeatureIds: string[],
  hideTextboxFeatures = false,
): void => {
  const idConditions = hiddenFeatureIds
    .map((id) => ['!=', 'id', id] as FilterSpecification);

  style.layers.forEach((layer) => {
    if (layer.type === 'background') {
      return;
    }

    const extraConditions: FilterSpecification[] = [...idConditions];

    if (hideTextboxFeatures && layer.id === LABEL_LAYER_ID) {
      extraConditions.push([
        '!=',
        'type',
        TEXTBOX_TYPE_VALUE,
      ] as FilterSpecification);
    }

    if (isEmpty(extraConditions)) {
      return;
    }

    const existingFilter = 'filter' in layer ? layer.filter : undefined;
    const existingConditions =
      Array.isArray(existingFilter) && existingFilter[0] === 'all'
        ? existingFilter.slice(1)
        : existingFilter
          ? [existingFilter]
          : [];

    const combined = [...existingConditions, ...extraConditions];

    layer.filter = (
      combined.length === 1 ? combined[0] : ['all', ...combined]
    ) as FilterSpecification;
  });
};

/**
 * Clones the layer templates and sets the given source ID.
 *
 * @param sourceId - The ID to assign as the `source` in each style layer
 * @param layerTemplates - Array of layer templates (fill/line specifications)
 * @returns Array of style layers with updated source
 */
export const createStyleLayers = (
  sourceId: string,
  layerTemplates: (FillLayerSpecification | LineLayerSpecification)[],
): (FillLayerSpecification | LineLayerSpecification)[] => {
  return layerTemplates.map(
    (templateLayer: FillLayerSpecification | LineLayerSpecification) => {
      const cloned = cloneDeep(templateLayer);
      cloned.source = sourceId;
      return cloned;
    },
  );
};

/**
 * Loads a workspace layer as vector tiles into Cesium.
 *
 * @param layer - The workspace layer to load
 * @param cesiumProxy - CesiumProxy instance
 * @param layerTemplates - Array of layer templates for styling
 * @returns The added imagery layer or undefined
 */
export const loadWorkspaceLayerAsVectorTile = async (
  layer: WorkspaceLayer,
  cesiumProxy: CesiumProxy,
  layerTemplates: (FillLayerSpecification | LineLayerSpecification)[],
): Promise<ImageryLayer | undefined> => {
  const sourceId = Object.keys(layer.mapLayerStyles?.sources ?? {})[0] ?? '';

  const configuredLayers = createStyleLayers(sourceId, layerTemplates);

  const styledLayer: WorkspaceLayer = {
    ...layer,
    mapLayerStyles: {
      ...layer.mapLayerStyles,
      layers: configuredLayers,
    } as StyleSpecification,
  };

  try {
    const imageryLayer =
      await cesiumProxy.layerManager?.addVectorTiledLayerToCesium(
        styledLayer,
        { show: true, zIndex: styledLayer.zIndex },
      );

    return imageryLayer;
  } catch (error) {
    console.error(`Failed to add layer ${layer.name}:`, error);
    return undefined;
  }
};

/**
 * Removes an imagery layer from Cesium.
 *
 * @param imageryLayer - The imagery layer to remove
 * @param cesiumProxy - CesiumProxy instance
 */
export const removeVectorTileLayer = (
  imageryLayer: ImageryLayer | null,
  cesiumProxy: CesiumProxy,
): void => {
  if (imageryLayer) {
    cesiumProxy.layerManager.removeImageryLayers([imageryLayer]);
  }
};
